/**
 * Frontend JavaScript for the Eguide block.
 * Handles form submission using cloudavocado-forms plugin.
 */

/* global cloudavocado_forms, grecaptcha */
/* eslint-disable camelcase, no-undef */
/* eslint-env browser */

'use strict';

/**
 * Show error message for a specific field.
 * @param {HTMLElement} fieldContainer
 * @param {string}      message
 */
function showFieldError( fieldContainer, message ) {
	const errorElement = fieldContainer.querySelector(
		'[class*="eguide-error-message-"]'
	);
	const placeholder = fieldContainer.querySelector(
		'.eguide-message-placeholder'
	);
	if ( errorElement ) {
		errorElement.textContent = message;
		errorElement.classList.remove( 'd-none' );
		errorElement.classList.add( 'd-block' );
	}

	// Hide placeholder when error is shown.
	if ( placeholder ) {
		placeholder.style.display = 'none';
	}
}

/**
 * Hide error message for a specific field.
 * @param {HTMLElement} fieldContainer
 */
function hideFieldError( fieldContainer ) {
	const errorElement = fieldContainer.querySelector(
		'[class*="eguide-error-message-"]'
	);
	const placeholder = fieldContainer.querySelector(
		'.eguide-message-placeholder'
	);
	if ( errorElement ) {
		errorElement.textContent = '';
		errorElement.classList.add( 'd-none' );
		errorElement.classList.remove( 'd-block' );
	}

	// Show placeholder if no error is visible.
	if ( placeholder ) {
		placeholder.style.display = 'block';
	}
}

/**
 * Show success message.
 * @param {HTMLElement} formContainer
 * @param {string}      message
 */
function showSuccess( formContainer, message ) {
	const successElement = formContainer.querySelector(
		'.eguide-success-message'
	);
	if ( successElement ) {
		successElement.textContent = message;
		successElement.classList.remove( 'd-none' );
		successElement.classList.add( 'd-block' );
	}

	// Hide only the success message placeholder (the one right before success element).
	const successPlaceholder = successElement
		? successElement.previousElementSibling
		: null;
	if (
		successPlaceholder &&
		successPlaceholder.classList.contains( 'eguide-message-placeholder' )
	) {
		successPlaceholder.style.display = 'none';
	}
}

/**
 * Hide success message.
 * @param {HTMLElement} formContainer
 */
function hideSuccess( formContainer ) {
	const successElement = formContainer.querySelector(
		'.eguide-success-message'
	);
	if ( successElement ) {
		successElement.textContent = '';
		successElement.classList.add( 'd-none' );
		successElement.classList.remove( 'd-block' );
	}

	// Show the success message placeholder again (the one right before success element).
	const successPlaceholder = successElement
		? successElement.previousElementSibling
		: null;
	if (
		successPlaceholder &&
		successPlaceholder.classList.contains( 'eguide-message-placeholder' )
	) {
		successPlaceholder.style.display = 'block';
	}
}

/**
 * Centralized error messages for the e-guide form.
 */
const ERROR_MESSAGES = {
	// Server error codes
	error_invalid_email: 'Please enter a valid email address.',
	error_required: 'This field is required.',
	error_min_length: 'This field is too short.',
	error_max_length: 'This field is too long.',
	error_invalid_nonce: 'Security check failed. Please refresh the page.',
	error_recaptcha_token: 'Security verification failed. Please try again.',
	error_unknown: 'Something went wrong. Please try again later.',

	// Email validation
	email_required: 'Please enter your email address.',
	email_invalid: 'Please enter a valid email address.',
	email_too_long: 'Email address is too long. Please use a shorter email.',

	// Text field validation
	field_required: 'This field is required.',
	field_too_long: 'This field is too long.',

	// Checkbox validation
	privacy_required: 'You must agree to the privacy policy.',

	// Form submission
	form_unavailable:
		'Form submission is not available. Please refresh the page.',
	submission_error: 'Something went wrong. Please try again later.',

	// Success messages
	download_starting: 'Your e-guide download will start in a moment.',
};

/**
 * Get error message from error code.
 * @param {string} errorCode
 * @return {string} Error message string
 */
function getErrorMessage( errorCode ) {
	return ERROR_MESSAGES[ errorCode ] || ERROR_MESSAGES.error_unknown;
}

/**
 * Validate email input.
 * @param {string} email
 * @return {Object} { isValid: boolean, errorMessage: string|null }
 */
function validateEmail( email ) {
	const trimmedEmail = email.trim();

	// Check if empty.
	if ( ! trimmedEmail ) {
		return {
			isValid: false,
			errorMessage: ERROR_MESSAGES.email_required,
		};
	}

	// Email format validation.
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	if ( ! emailRegex.test( trimmedEmail ) ) {
		return {
			isValid: false,
			errorMessage: ERROR_MESSAGES.email_invalid,
		};
	}

	// Length validation (max 254 characters as per plugin).
	if ( trimmedEmail.length > 254 ) {
		return {
			isValid: false,
			errorMessage: ERROR_MESSAGES.email_too_long,
		};
	}

	return {
		isValid: true,
		errorMessage: null,
	};
}

/**
 * Validate text input.
 * @param {string}  value
 * @param {boolean} required
 * @return {Object} { isValid: boolean, errorMessage: string|null }
 */
function validateText( value, required = false ) {
	const trimmedValue = value.trim();

	// Check if required and empty.
	if ( required && ! trimmedValue ) {
		return {
			isValid: false,
			errorMessage: ERROR_MESSAGES.field_required,
		};
	}

	// Length validation (max 254 characters as per plugin).
	if ( trimmedValue.length > 254 ) {
		return {
			isValid: false,
			errorMessage: ERROR_MESSAGES.field_too_long,
		};
	}

	return {
		isValid: true,
		errorMessage: null,
	};
}

/**
 * Validate checkbox.
 * @param {boolean} checked
 * @return {Object} { isValid: boolean, errorMessage: string|null }
 */
function validateCheckbox( checked ) {
	if ( ! checked ) {
		return {
			isValid: false,
			errorMessage: ERROR_MESSAGES.privacy_required,
		};
	}

	return {
		isValid: true,
		errorMessage: null,
	};
}

// func which check if all fields in form is valid & return true or false
const isAllFieldValid = ( form ) => {
	const emailInput = form.querySelector( 'input[name="email"]' );
	const companyInput = form.querySelector( 'input[name="company"]' );
	const roleInput = form.querySelector( 'input[name="role"]' );
	const privacyInput = form.querySelector( 'input[name="privacy"]' );

	// Validate all fields.
	const emailValidation = validateEmail( emailInput.value );
	const companyValidation = validateText( companyInput.value, false );
	const roleValidation = validateText( roleInput.value, false );
	const privacyValidation = validateCheckbox( privacyInput.checked );

	return (
		emailValidation &&
		companyValidation &&
		roleValidation &&
		privacyValidation
	);
};

/**
 * Handle input change - real-time validation.
 * @param {Event} event
 */
function handleInputChange( event ) {
	const input = event.target;
	const fieldContainer = input.closest( '.form-input' );
	const form = input.closest( 'form' );
	const formContainer = form ? form.closest( '.eguide-form' ) : null;

	if ( ! fieldContainer || ! form ) {
		return;
	}

	// Hide success message when user starts editing.
	if ( formContainer ) {
		hideSuccess( formContainer );
	}

	const fieldName = input.name;
	let validation;

	if ( fieldName === 'email' ) {
		validation = validateEmail( input.value );
	} else if ( fieldName === 'privacy' ) {
		validation = validateCheckbox( input.checked );
	} else {
		validation = validateText( input.value, false );
	}

	const submitButton = form.querySelector( 'button[type="submit"]' );

	if ( validation.isValid ) {
		hideFieldError( fieldContainer );
		submitButton.disabled = ! isAllFieldValid( form );
	} else {
		showFieldError( fieldContainer, validation.errorMessage );
		submitButton.disabled = true;
	}
}

/**
 * Handle form submission.
 * @param {Event} event
 */
function handleFormSubmit( event ) {
	event.preventDefault();

	const form = event.target;
	const formContainer = form.closest( '.eguide-form' );
	const emailInput = form.querySelector( 'input[name="email"]' );
	const companyInput = form.querySelector( 'input[name="company"]' );
	const roleInput = form.querySelector( 'input[name="role"]' );
	const privacyInput = form.querySelector( 'input[name="privacy"]' );

	// Mark form as validated so real-time validation can work.
	form.setAttribute( 'data-validated', 'true' );

	// Hide all field errors first.
	const allFieldContainers = form.querySelectorAll( '.form-input' );
	allFieldContainers.forEach( ( container ) => {
		hideFieldError( container );
	} );
	hideSuccess( formContainer );

	// Validate all fields.
	const emailValidation = validateEmail( emailInput.value );
	const companyValidation = validateText( companyInput.value, false );
	const roleValidation = validateText( roleInput.value, false );
	const privacyValidation = validateCheckbox( privacyInput.checked );

	let hasErrors = false;

	// Show errors for invalid fields.
	if ( ! emailValidation.isValid ) {
		const emailContainer = emailInput.closest( '.form-input' );
		showFieldError( emailContainer, emailValidation.errorMessage );
		hasErrors = true;
	}

	if ( ! companyValidation.isValid ) {
		const companyContainer = companyInput.closest( '.form-input' );
		showFieldError( companyContainer, companyValidation.errorMessage );
		hasErrors = true;
	}

	if ( ! roleValidation.isValid ) {
		const roleContainer = roleInput.closest( '.form-input' );
		showFieldError( roleContainer, roleValidation.errorMessage );
		hasErrors = true;
	}

	if ( ! privacyValidation.isValid ) {
		const privacyContainer = privacyInput.closest( '.form-input' );
		showFieldError( privacyContainer, privacyValidation.errorMessage );
		hasErrors = true;
	}

	const submitButton = form.querySelector( 'button[type="submit"]' );

	if ( hasErrors ) {
		// Focus on first invalid field.
		if ( ! emailValidation.isValid ) {
			emailInput.focus();
		} else if ( ! privacyValidation.isValid ) {
			privacyInput.focus();
		} else if ( ! companyValidation.isValid ) {
			companyInput.focus();
		} else if ( ! roleValidation.isValid ) {
			roleInput.focus();
		}
		submitButton.disabled = true;
		return;
	}

	// Disable form during submission.
	emailInput.disabled = true;
	companyInput.disabled = true;
	roleInput.disabled = true;
	privacyInput.disabled = true;
	submitButton.disabled = true;
	const originalButtonText = submitButton.textContent;
	submitButton.textContent = 'Submitting...';

	// Check if cloudavocado_forms is available.
	if ( typeof cloudavocado_forms === 'undefined' ) {
		showFieldError(
			emailInput.closest( '.form-input' ),
			ERROR_MESSAGES.form_unavailable
		);
		emailInput.disabled = false;
		companyInput.disabled = false;
		roleInput.disabled = false;
		privacyInput.disabled = false;
		submitButton.disabled = false;
		submitButton.textContent = originalButtonText;
		return;
	}

	// Build message with form data.
	const company = companyInput.value.trim() || 'N/A';
	const role = roleInput.value.trim() || 'N/A';
	const message = `I am interested in e-guide.\n\nCompany: ${ company }\nRole: ${ role }`;

	// Prepare form data.
	const formData = {
		action: 'cloudavocado_forms_submit_contact_form',
		nonce: cloudavocado_forms.nonce,
		first_name: company,
		last_name: 'N/A',
		email: emailInput.value.trim(),
		message,
	};

	// Handle reCAPTCHA if enabled.
	const handleSubmission = ( recaptchaToken = '' ) => {
		if ( recaptchaToken ) {
			formData.recaptcha_token = recaptchaToken;
		}

		// Submit form.
		fetch( cloudavocado_forms.ajax_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: new URLSearchParams( formData ),
		} )
			.then( ( response ) => response.json() )
			.then( ( data ) => {
				if ( data.success ) {
					// Success - clear inputs and show success message.
					emailInput.value = '';
					companyInput.value = '';
					roleInput.value = '';
					privacyInput.checked = false;
					emailInput.disabled = false;
					companyInput.disabled = false;
					roleInput.disabled = false;
					privacyInput.disabled = false;
					submitButton.disabled = false;
					submitButton.textContent = originalButtonText;
					form.removeAttribute( 'data-validated' );

					// Trigger PDF download if PDF URL is available.
					const pdfUrl = formContainer.dataset.pdfUrl;
					if ( pdfUrl ) {
						// Show success message first
						showSuccess(
							formContainer,
							ERROR_MESSAGES.download_starting
						);
						// Start download after a brief delay to ensure message is visible
						setTimeout( () => {
							downloadPDF( pdfUrl );
						}, 300 );
					} else {
						// No PDF configured, show generic message.
						showSuccess(
							formContainer,
							ERROR_MESSAGES.download_starting
						);
					}

					// Show all field placeholders again after successful submission.
					const fieldPlaceholders = form.querySelectorAll(
						'.form-input .eguide-message-placeholder'
					);
					fieldPlaceholders.forEach( ( placeholder ) => {
						placeholder.style.display = 'block';
					} );
				} else {
					// Error - show error message.
					const errorCode = data.data?.code || 'error_unknown';
					const errorMessage = getErrorMessage( errorCode );
					const errorFields = data.data?.fields || [];

					// Show errors for specific fields if provided.
					if ( errorFields.includes( 'email' ) ) {
						showFieldError(
							emailInput.closest( '.form-input' ),
							errorMessage
						);
					} else if ( errorFields.length > 0 ) {
						// Show error on first field if no specific field provided.
						showFieldError(
							emailInput.closest( '.form-input' ),
							errorMessage
						);
					} else {
						showFieldError(
							emailInput.closest( '.form-input' ),
							errorMessage
						);
					}

					emailInput.disabled = false;
					companyInput.disabled = false;
					roleInput.disabled = false;
					privacyInput.disabled = false;
					submitButton.disabled = false;
					submitButton.textContent = originalButtonText;
					emailInput.focus();
				}
			} )
			.catch( () => {
				showFieldError(
					emailInput.closest( '.form-input' ),
					ERROR_MESSAGES.submission_error
				);
				emailInput.disabled = false;
				companyInput.disabled = false;
				roleInput.disabled = false;
				privacyInput.disabled = false;
				submitButton.disabled = false;
				submitButton.textContent = originalButtonText;
				emailInput.focus();
			} );
	};

	// Check if reCAPTCHA is enabled.
	if (
		cloudavocado_forms.recaptcha_site_key &&
		typeof grecaptcha !== 'undefined'
	) {
		grecaptcha.ready( function () {
			grecaptcha
				.execute( cloudavocado_forms.recaptcha_site_key, {
					action: 'submit',
				} )
				.then( function ( token ) {
					handleSubmission( token );
				} )
				.catch( function () {
					// Show error and re-enable form.
					showFieldError(
						emailInput.closest( '.form-input' ),
						ERROR_MESSAGES.submission_error
					);
					emailInput.disabled = false;
					companyInput.disabled = false;
					roleInput.disabled = false;
					privacyInput.disabled = false;
					submitButton.disabled = false;
					submitButton.textContent = originalButtonText;
				} );
		} );
	} else {
		// No reCAPTCHA, submit directly.
		handleSubmission();
	}
}

/**
 * Download PDF file.
 * @param {string} url PDF file URL
 */
function downloadPDF( url ) {
	if ( ! url ) {
		return;
	}

	try {
		// Create temporary link and trigger download.
		const link = document.createElement( 'a' );
		link.href = url;
		link.download = url.split( '/' ).pop() || 'eguide.pdf';
		link.target = '_blank';
		link.rel = 'noopener noreferrer';
		link.style.display = 'none';

		document.body.appendChild( link );
		link.click();

		// Clean up after a short delay
		setTimeout( () => {
			document.body.removeChild( link );
		}, 100 );
	} catch ( error ) {
		// Fallback: open in new tab if download fails
		window.open( url, '_blank', 'noopener,noreferrer' );
	}
}

/**
 * Initialize all eguide forms on the page.
 */
function initEguideForms() {
	const forms = document.querySelectorAll(
		'.eguide-form-inner[data-form-type="eguide"]'
	);
	forms.forEach( ( form ) => {
		form.addEventListener( 'submit', handleFormSubmit );

		// Add real-time validation on input change.
		const inputs = form.querySelectorAll( 'input' );
		inputs.forEach( ( input ) => {
			if ( input.type === 'checkbox' ) {
				input.addEventListener( 'change', handleInputChange );
			} else {
				input.addEventListener( 'input', handleInputChange );
			}
		} );
	} );
}

// Initialize when DOM is ready.
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initEguideForms );
} else {
	initEguideForms();
}
